/**
 * Popover — anchored beside its trigger, never a modal (spec Decision 2, "Not modals").
 * variant="picker" (add relationship, set parent, choose capability) is 320px, inside the spec's
 * 280–360px; "menu" (Export, Views, Columns) and "filter" (a column filter) size to content.
 * Every popup carries data-kit="popover" + data-variant; the conformance test exempts menu and
 * filter popovers from the picker width.
 */
import * as React from 'react';
import { Popover as BasePopover } from '@base-ui-components/react/popover';

export type PopoverVariant = 'picker' | 'menu' | 'filter';

export interface PopoverProps {
  /** The trigger: a kit Button (or anything that takes a ref and onClick). */
  trigger: React.ReactElement;
  children: React.ReactNode;
  variant?: PopoverVariant;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  'aria-label'?: string;
  className?: string;
}

export function Popover({ trigger, children, variant = 'menu', open, onOpenChange, align = 'start', side = 'bottom', className, ...rest }: PopoverProps) {
  return (
    <BasePopover.Root open={open} onOpenChange={onOpenChange ? (o: boolean) => onOpenChange(o) : undefined}>
      <BasePopover.Trigger render={trigger} />
      <BasePopover.Portal>
        <BasePopover.Positioner align={align} side={side} sideOffset={4} collisionPadding={8} className="k-pos">
          <BasePopover.Popup
            className={className ? `k-pop ${className}` : 'k-pop'}
            data-kit="popover"
            data-variant={variant}
            aria-label={rest['aria-label']}
          >
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

/** A row in a popover: a choice, a checkbox row or an action. */
export function PopoverItem({ children, onSelect, selected, className, ...rest }: Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> & { onSelect?: () => void; selected?: boolean }) {
  return (
    <button type="button" data-kit="option" className={className ? `k-pop-item ${className}` : 'k-pop-item'} aria-selected={selected} onClick={onSelect} {...rest}>
      {children}
    </button>
  );
}

export function PopoverHeading({ children }: { children: React.ReactNode }) {
  return <p className="k-pop-head">{children}</p>;
}

export function PopoverSeparator() {
  return <div className="k-pop-sep" role="separator" />;
}
